import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Paper, CircularProgress, Alert, Button, Grid, Accordion, AccordionSummary, AccordionDetails, Chip, Tooltip, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, RadioGroup, FormControlLabel, Radio, TextField, Snackbar, Card, CardContent, Divider } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';

// Helper function to get color based on score
const getScoreColor = (score) => {
  if (score >= 93.01) return '#4caf50'; // Green
  if (score >= 80) return '#ff9800'; // Orange
  return '#f44336'; // Red
};

// Helper function to get background color based on score
const getScoreBackground = (score) => {
  if (score >= 93.01) return '#e8f5e8'; // Light green
  if (score >= 80) return '#fff3e0'; // Light orange
  return '#ffebee'; // Light red
};

// Helper function to get sentence color based on score
const getSentenceScoreColor = (score) => {
  if (score >= 95) return '#4caf50'; // Green
  if (score >= 80) return '#ff9800'; // Orange
  return '#f44336'; // Red
};

// Helper function to get sentence background color based on score
const getSentenceScoreBackground = (score) => {
  if (score >= 95) return '#e8f5e8'; // Light green
  if (score >= 80) return '#fff3e0'; // Light orange
  return '#ffebee'; // Light red
};

// Helper function to remove punctuation from words
const removePunctuation = (word) => {
  return word.replace(/[^\w\s]/g, '');
};

// Helper function to format reading time in minutes and seconds
const formatReadingTime = (seconds) => {
  if (!seconds || seconds === 0) return 'N/A';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  } else {
    return `${remainingSeconds}s`;
  }
};

// Helper function to get actual score for Whisper words
const getWordScore = (word, sentenceIndex, wordIndex, gradingMethod, sentenceAttempts) => {
  // Check if word is overridden
  if (word.override && word.override.isOverridden) {
    if (word.override.overrideType === 'correct' || word.override.overrideType === 'accent') {
      return 100; // Override to correct
    }
    // For 'correction' and 'incorrect' types, count as incorrect
    if (word.override.overrideType === 'correction' || word.override.overrideType === 'incorrect') {
      return 0; // Count as incorrect
    }
  }
  
  if (gradingMethod === 'whisper') {
    // For Whisper, use the isCorrect field primarily, but also check GPT analysis for more nuanced scoring
    if (word.isCorrect) {
      return 100;
    }
    
    // For incorrect words, try to get a more nuanced score from GPT analysis
    const attempt = sentenceAttempts[sentenceIndex];
    if (attempt?.gptAnalysisResponse?.miscues) {
      // Find miscue by matching the transcribed word (ignoring punctuation)
      const targetWord = word.targetWord.replace(/[,.]$/, '').toLowerCase();
      
      const miscue = attempt.gptAnalysisResponse.miscues.find(m => 
        m && m.targetWord && m.targetWord.toLowerCase() === targetWord
      );
      if (miscue && miscue.score) {
        return parseInt(miscue.score) || 0;
      }
    }
    
    return 0; // Default for incorrect words
  } else {
    return word.qualityScore;
  }
};

// Helper function to get miscue reason for Whisper words
const getMiscueReason = (sentenceIndex, wordIndex, gradingMethod, sentenceAttempts, targetWord, transcribedWord) => {
  if (gradingMethod === 'whisper') {
    const attempt = sentenceAttempts[sentenceIndex];
    if (attempt?.gptAnalysisResponse?.miscues) {
      // Find miscue by matching the transcribed word (ignoring punctuation)
      const normalizedTarget = targetWord.replace(/[,.]$/, '').toLowerCase();
      
      const miscue = attempt.gptAnalysisResponse.miscues.find(m => 
        m && m.targetWord && m.targetWord.toLowerCase() === normalizedTarget
      );
      
      if (miscue && miscue.reason) {
        return miscue.reason;
      }
    }
    
    // Generate a basic reason based on the word analysis
    if (transcribedWord === '') {
      return 'Word was omitted from reading';
    }
    return 'Word was read differently than expected';
  }
  return 'Pronunciation issue detected';
};

// Enhanced Word Tooltip Component
const EnhancedWordTooltip = ({ 
  word, 
  displayWord, 
  qualityScore, 
  gradingMethod, 
  isOmitted, 
  isOverridden, 
  sentenceIndex, 
  wordIndex, 
  linkId, 
  playingAudio, 
  onPlayAudio, 
  onQuickOverride,
  children 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isButtonInteracting, setIsButtonInteracting] = useState(false); // LOCK tooltip during button clicks
  const hoverTimeoutRef = useRef(null);
  const audioTimeoutRef = useRef(null);
  const clickProcessedRef = useRef(false);

  const audioKey = `${sentenceIndex}-${wordIndex}`;
  const isCurrentlyPlaying = playingAudio === audioKey;

  const handleMouseEnter = () => {
    setIsHovering(true);
    setIsOpen(true);
    
    // Clear any existing timeouts
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // DISABLE AUTO-PLAY - it was interfering with button clicks
    // Teachers will manually click play when they want to hear it
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    
    // Clear audio timeout if leaving before auto-play
    if (audioTimeoutRef.current) {
      clearTimeout(audioTimeoutRef.current);
    }

    // Much longer delay to ensure button clicks work
    hoverTimeoutRef.current = setTimeout(() => {
      if (!isHovering) {
        console.log('🚪 Closing tooltip after mouse leave');
        setIsOpen(false);
      } else {
        console.log('🔒 Keeping tooltip open - still hovering');
      }
    }, 1000); // Even longer delay
  };

  const handleTooltipMouseEnter = () => {
    console.log('🎯 Mouse entered tooltip');
    setIsHovering(true);
    // Clear any close timeout when entering tooltip
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  };

  const handleTooltipMouseLeave = (e) => {
    console.log('🚪 Mouse left tooltip, relatedTarget:', e?.relatedTarget?.tagName, 'buttonInteracting:', isButtonInteracting);
    
    // NEVER close if button is being interacted with
    if (isButtonInteracting) {
      console.log('🔒 LOCKED - Button interaction in progress, tooltip stays open');
      return;
    }
    
    // Don't close if mouse is moving to a button
    if (e?.relatedTarget?.tagName === 'BUTTON' || 
        e?.relatedTarget?.closest('button') ||
        e?.relatedTarget?.classList?.contains('MuiButton-root')) {
      console.log('🛑 NOT closing tooltip - mouse moved to button');
      return;
    }
    
    setIsHovering(false);
    // Close after leaving tooltip - but with longer delay for button clicks
    hoverTimeoutRef.current = setTimeout(() => {
      if (!isButtonInteracting) { // Double check
        console.log('🔚 Actually closing tooltip after delay');
        setIsOpen(false);
      } else {
        console.log('🔒 Still locked by button interaction');
      }
    }, 500); // Longer delay
  };

  const handleManualPlay = (e) => {
    console.log('🎵 MANUAL PLAY clicked - BEFORE audio play', {
      isButtonInteracting,
      isHovering,
      isOpen,
      playingAudio,
      audioKey
    });
    
    e.stopPropagation();
    if (audioTimeoutRef.current) {
      clearTimeout(audioTimeoutRef.current);
    }
    
    // Reset any click processing state when playing audio
    clickProcessedRef.current = false;
    
    onPlayAudio(audioKey, sentenceIndex, wordIndex);
    
    // Check state after audio starts
    setTimeout(() => {
      console.log('🎵 Audio play triggered - AFTER audio play', {
        isButtonInteracting,
        isHovering,
        isOpen,
        playingAudio
      });
    }, 100);
  };

  const handleQuickCorrect = (e) => {
    console.log('🚀 ONCLICK FIRED!!! This is the actual click handler', {
      eventType: e.type,
      sentenceIndex,
      wordIndex, 
      targetWord: word.targetWord || word.word,
      timestamp: Date.now()
    });

    e.stopPropagation();
    e.preventDefault();
    
    // Clear any timeouts
    if (audioTimeoutRef.current) {
      clearTimeout(audioTimeoutRef.current);
    }
    
    // Make sure the button is actionable
    if (isOmitted || qualityScore >= 93.01) {
      console.warn('⚠️ Button should not be clickable for this word');
      return;
    }
    
    console.log('📞 About to call onQuickOverride with:', {
      sentenceIndex,
      wordIndex,
      targetWord: word.targetWord || word.word,
      overrideType: 'correct'
    });
    
    try {
      onQuickOverride({
        sentenceIndex,
        wordIndex,
        targetWord: word.targetWord || word.word,
        overrideType: 'correct'
      });
      console.log('✅ onQuickOverride called successfully');
    } catch (error) {
      console.error('❌ Error in handleQuickCorrect:', error);
    }
  };

  const handleAdvancedOverride = (e) => {
    e.stopPropagation();
    onQuickOverride({
      sentenceIndex,
      wordIndex,
      targetWord: word.targetWord || word.word,
      showDialog: true
    });
  };

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      if (audioTimeoutRef.current) clearTimeout(audioTimeoutRef.current);
    };
  }, []);

  const tooltipContent = (
    <Card 
      sx={{ 
        minWidth: 320, 
        maxWidth: 400, 
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        border: '1px solid rgba(0,0,0,0.08)'
      }}
      onMouseEnter={handleTooltipMouseEnter}
      onMouseLeave={handleTooltipMouseLeave}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight={700} color="primary">
            "{displayWord}"
          </Typography>
          <Chip 
            label={`${qualityScore}%`}
            size="small"
            sx={{ 
              backgroundColor: getScoreBackground(qualityScore),
              color: getScoreColor(qualityScore),
              fontWeight: 700
            }}
          />
        </Box>

        {/* Word Analysis */}
        <Box mb={2}>
          {gradingMethod === 'whisper' && (
            <Grid container spacing={1} mb={1}>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary" fontWeight={600}>
                  Target:
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                  "{removePunctuation(word.targetWord)}"
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary" fontWeight={600}>
                  Transcribed:
                </Typography>
                <Typography variant="body2" sx={{ 
                  wordBreak: 'break-word',
                  color: isOmitted ? '#d32f2f' : 'inherit',
                  fontStyle: isOmitted ? 'italic' : 'normal'
                }}>
                  {word.transcribedWord ? `"${removePunctuation(word.transcribedWord)}"` : 'OMITTED'}
                </Typography>
              </Grid>
            </Grid>
          )}

          <Typography variant="caption" color="textSecondary" fontWeight={600}>
            Status:
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {isOmitted ? '❌ Omitted' : 
             isOverridden ? '✅ Teacher Override' :
             qualityScore >= 93.01 ? '✅ Correct' : 
             qualityScore >= 80 ? '⚠️ Needs practice' : '❌ Incorrect'}
          </Typography>

          {isOverridden && (
            <Box sx={{
              backgroundColor: word.override.overrideType === 'incorrect' ? '#ffebee' : '#e8f5e8',
              p: 1,
              borderRadius: 1,
              border: word.override.overrideType === 'incorrect' ? '1px solid #f44336' : '1px solid #4caf50',
              mb: 1
            }}>
              <Typography variant="caption" color={word.override.overrideType === 'incorrect' ? 'error.dark' : 'success.dark'} fontWeight={700}>
                Teacher Override: {
                  word.override.overrideType === 'correct' ? 'Marked Correct' :
                  word.override.overrideType === 'accent' ? 'Accent Variant' :
                  word.override.overrideType === 'incorrect' ? 'Marked Incorrect' :
                  word.override.overrideType === 'correction' ? `Corrected: ${word.override.correctedWord}` :
                  `Corrected: ${word.override.correctedWord}`
                }
              </Typography>
              {word.override.reason && (
                <Typography variant="caption" display="block" color="textSecondary">
                  Note: {word.override.reason}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Action Buttons */}
        <Box>
          {/* Audio Controls */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="subtitle2" fontWeight={600}>
              Audio Playback:
            </Typography>
            {isOmitted ? (
              <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                No audio (word was omitted)
              </Typography>
            ) : !word.timing || word.timing.start === null || word.timing.start === undefined ? (
              <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                No audio timing available
              </Typography>
            ) : (
              <Button
                size="small"
                variant={isCurrentlyPlaying ? "contained" : "outlined"}
                startIcon={isCurrentlyPlaying ? <PauseIcon /> : <VolumeUpIcon />}
                onClick={handleManualPlay}
                sx={{ minWidth: 100 }}
              >
                {isCurrentlyPlaying ? "Playing..." : "Play Audio"}
              </Button>
            )}
          </Box>

          {/* Quick Override Actions - only show for incorrect words that aren't already overridden */}
          {!isOverridden && !isOmitted && qualityScore < 93.01 && (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>
                Quick Override:
              </Typography>
              <Button
                size="small"
                fullWidth
                variant="contained"
                startIcon={<CheckIcon />}
                onMouseDown={(e) => {
                  console.log('🚀 MOUSEDOWN - FORCE PROCESSING OVERRIDE (bypass onClick)');
                  
                  // Process immediately on mouseDown to avoid any onClick issues
                  e.stopPropagation();
                  e.preventDefault();
                  
                  if (!clickProcessedRef.current) {
                    clickProcessedRef.current = true;
                    setIsButtonInteracting(true);
                    
                    console.log('⚡ FORCING OVERRIDE IMMEDIATELY via mouseDown');
                    handleQuickCorrect(e);
                    
                    setTimeout(() => { 
                      clickProcessedRef.current = false;
                      setIsButtonInteracting(false);
                      console.log('🔓 UNLOCKING after forced override');
                    }, 2000);
                  }
                }}
                onClick={(e) => {
                  console.log('🚀 ONCLICK FIRED!!! (secondary handler)', {
                    eventType: e.type,
                    clickProcessed: clickProcessedRef.current,
                    timestamp: Date.now()
                  });
                  
                  // onClick as fallback only
                  if (!clickProcessedRef.current) {
                    console.log('🔄 Processing via onClick fallback');
                    clickProcessedRef.current = true;
                    handleQuickCorrect(e);
                    
                    setTimeout(() => { 
                      clickProcessedRef.current = false;
                      setIsButtonInteracting(false);
                    }, 1500);
                  }
                }}
                onMouseUp={(e) => {
                  console.log('🔄 MOUSEUP - Should be processed already by mouseDown');
                  // MouseDown should have handled everything
                }}
                onMouseEnter={(e) => {
                  console.log('🖱️ Button mouse enter - keeping tooltip open');
                  if (hoverTimeoutRef.current) {
                    clearTimeout(hoverTimeoutRef.current);
                  }
                  setIsHovering(true);
                }}
                onMouseLeave={(e) => {
                  console.log('🖱️ Button mouse leave');
                  // Don't immediately close - let the tooltip handle it
                }}
                sx={{ 
                  fontSize: '0.8rem', 
                  mb: 1,
                  transition: 'all 0.1s ease',
                  '&:active': {
                    transform: 'scale(0.95)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  },
                  // Ensure button stays clickable above everything else
                  position: 'relative',
                  zIndex: 10000
                }}
                color="success"
              >
                Mark Correct
              </Button>
              <Button
                size="small"
                fullWidth
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={handleAdvancedOverride}
                sx={{ fontSize: '0.75rem' }}
              >
                Advanced Override...
              </Button>
            </Box>
          )}

          {/* For correct words, show option to mark as incorrect */}
          {!isOverridden && !isOmitted && qualityScore >= 93.01 && (
            <Box sx={{ p: 1 }}>
              <Typography variant="body2" color="success.main" sx={{ fontStyle: 'italic', mb: 1, textAlign: 'center' }}>
                ✅ This word was read correctly!
              </Typography>
              <Button
                size="small"
                fullWidth
                variant="outlined"
                color="error"
                startIcon={<ClearIcon />}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  if (!clickProcessedRef.current) {
                    clickProcessedRef.current = true;
                    setIsButtonInteracting(true);

                    console.log('⚡ Marking correct word as incorrect');
                    onQuickOverride({
                      sentenceIndex,
                      wordIndex,
                      targetWord: word.targetWord || word.word,
                      overrideType: 'incorrect'
                    });

                    setTimeout(() => {
                      clickProcessedRef.current = false;
                      setIsButtonInteracting(false);
                    }, 2000);
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!clickProcessedRef.current) {
                    clickProcessedRef.current = true;
                    onQuickOverride({
                      sentenceIndex,
                      wordIndex,
                      targetWord: word.targetWord || word.word,
                      overrideType: 'incorrect'
                    });

                    setTimeout(() => {
                      clickProcessedRef.current = false;
                      setIsButtonInteracting(false);
                    }, 1500);
                  }
                }}
                onMouseEnter={(e) => {
                  if (hoverTimeoutRef.current) {
                    clearTimeout(hoverTimeoutRef.current);
                  }
                  setIsHovering(true);
                }}
                sx={{
                  fontSize: '0.75rem',
                  transition: 'all 0.1s ease',
                  '&:active': {
                    transform: 'scale(0.95)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  },
                  position: 'relative',
                  zIndex: 10000
                }}
              >
                Mark as Incorrect
              </Button>
            </Box>
          )}

          {/* Remove Override */}
          {isOverridden && (
            <Button
              size="small"
              fullWidth
              variant="outlined"
              color="error"
              startIcon={<ClearIcon />}
              onClick={(e) => {
                e.stopPropagation();
                onQuickOverride({
                  sentenceIndex,
                  wordIndex,
                  removeOverride: true
                });
              }}
              sx={{ fontSize: '0.75rem' }}
            >
              Remove Override
            </Button>
          )}

          {/* Detailed Analysis for SpeechAce */}
          {gradingMethod === 'speechace' && word.phoneScores && word.phoneScores.length > 0 && (
            <Box mt={2} sx={{ backgroundColor: '#f8f9fa', p: 1.5, borderRadius: 1 }}>
              <Typography variant="caption" fontWeight={600} mb={1} display="block">
                Phoneme Analysis:
              </Typography>
              {word.phoneScores.slice(0, 3).map((phone, phoneIndex) => (
                <Typography key={phoneIndex} variant="caption" display="block" sx={{ mb: 0.5 }}>
                  /{phone.phone}/: {phone.qualityScore.toFixed(1)}%
                  {phone.soundMostLike && phone.soundMostLike !== phone.phone && 
                    ` → /${phone.soundMostLike}/`
                  }
                </Typography>
              ))}
              {word.phoneScores.length > 3 && (
                <Typography variant="caption" color="textSecondary">
                  ... and {word.phoneScores.length - 3} more
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Tooltip
      title={tooltipContent}
      open={isOpen}
      onClose={() => setIsOpen(false)}
      placement="top"
      arrow
      componentsProps={{
        tooltip: {
          sx: { 
            backgroundColor: 'transparent',
            boxShadow: 'none',
            p: 0
          }
        },
        arrow: {
          sx: { color: '#fff' }
        }
      }}
    >
      <Box
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{ display: 'inline-block' }}
      >
        {children}
      </Box>
    </Tooltip>
  );
};

// Helper function to split text into sentences for internal breakdown
const splitIntoSentences = (text) => {
  if (!text) return [];
  
  // Split by sentence endings, keeping the punctuation
  const sentences = text.split(/(?<=[.!?])\s+/)
    .filter(sentence => sentence.trim().length > 0)
    .map(sentence => sentence.trim());
    
  return sentences;
};

// Helper function to get sentence boundaries from word analysis timing
const getSentenceBoundariesFromTiming = (wordAnalysis, sentenceText, transcribedText) => {
  // Reduced logging - only show key info
  console.log('📝 Processing paragraph with', wordAnalysis?.length || 0, 'words');
  
  if (!wordAnalysis || wordAnalysis.length === 0) return [];
  
  const sentences = splitIntoSentences(sentenceText);
  
  if (sentences.length <= 1) return [{ 
    text: sentenceText, 
    words: wordAnalysis, 
    transcribedText: transcribedText || sentenceText 
  }];
  
  // Split transcribed text into sentences - try to match the original sentence structure
  const transcribedSentences = transcribedText ? splitIntoSentences(transcribedText) : sentences;
  
  // Create a mapping of sentence boundaries using word content matching instead of counting
  const sentenceBoundaries = [];
  
  // Create a flat list of all words from all sentences to match against word analysis
  const allSentenceWords = [];
  sentences.forEach((sentence, sentenceIdx) => {
    const words = sentence.split(/\s+/).filter(word => word.length > 0);
    words.forEach((word, wordIdx) => {
      allSentenceWords.push({
        sentenceIdx,
        wordIdx,
        text: word.toLowerCase().replace(/[^\w]/g, ''), // Normalize for matching
        originalText: word
      });
    });
  });
  
  // Match word analysis entries to sentence words
  const wordToSentenceMapping = [];
  let mappedWordsCount = 0;
  let unmatchedWords = 0;
  
  wordAnalysis.forEach((analysisWord, analysisIdx) => {
    if (analysisWord && analysisWord.targetWord) {
      const normalizedAnalysisWord = analysisWord.targetWord.toLowerCase().replace(/[^\w]/g, '');
      
      // Find matching sentence word that hasn't been mapped yet
      const matchIdx = allSentenceWords.findIndex((sentenceWord, idx) => 
        idx >= mappedWordsCount && // Only look at unmapped words
        sentenceWord.text === normalizedAnalysisWord
      );
      
      if (matchIdx !== -1) {
        wordToSentenceMapping[analysisIdx] = allSentenceWords[matchIdx].sentenceIdx;
        mappedWordsCount = matchIdx + 1;
      } else {
        // Fallback: estimate based on word order
        const estimatedSentence = Math.floor((analysisIdx / wordAnalysis.length) * sentences.length);
        wordToSentenceMapping[analysisIdx] = Math.min(estimatedSentence, sentences.length - 1);
        unmatchedWords++;
      }
    }
  });
  
  if (unmatchedWords > 0) {
    console.warn(`⚠️ ${unmatchedWords} words could not be exactly matched and were estimated`);
  }
  
  // Group word analysis by sentence
  sentences.forEach((sentence, sentenceIdx) => {
    const sentenceWordsWithIndices = [];
    wordAnalysis.forEach((word, analysisIdx) => {
      if (wordToSentenceMapping[analysisIdx] === sentenceIdx) {
        sentenceWordsWithIndices.push({
          ...word,
          originalWordIndex: analysisIdx // Store the original index
        });
      }
    });
    
    // Use corresponding transcribed sentence if available
    const sentenceTranscribed = transcribedSentences[sentenceIdx] || 
                               sentenceWordsWithIndices
                                 .map(w => w.transcribedWord && w.transcribedWord !== '[un-analyzed]' ? w.transcribedWord : '')
                                 .filter(w => w.trim().length > 0)
                                 .join(' ') ||
                               sentence;
    
    sentenceBoundaries.push({
      text: sentence,
      words: sentenceWordsWithIndices,
      transcribedText: sentenceTranscribed,
      startWordIndex: -1, // Not applicable with content-based matching
      endWordIndex: -1    // Not applicable with content-based matching
    });
  });
  
  console.log('✅ Processed', sentenceBoundaries.length, 'sentences with word mapping complete');
  
  return sentenceBoundaries;
};

export default function TestReport() {
  const { linkId } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [audioElements, setAudioElements] = useState({});
  const [overrideDialog, setOverrideDialog] = useState({ open: false, wordData: null });
  const [overrideType, setOverrideType] = useState('');
  const [correctedWord, setCorrectedWord] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [overriddenWords, setOverriddenWords] = useState(new Set()); // Track overridden words for highlighting
  const [regeneratingSentence, setRegeneratingSentence] = useState(null); // Track which sentence is being regenerated
  const [regenerationStatus, setRegenerationStatus] = useState('');
  const [regenerationDialog, setRegenerationDialog] = useState({ open: false, sentenceData: null });
  const [recentlyRegeneratedSentences, setRecentlyRegeneratedSentences] = useState(new Set()); // Track recently regenerated sentences
  const navigate = useNavigate();

  // Check if report is still processing with detailed status
  const checkProcessingStatus = async () => {
    try {
      const response = await api.get(`/api/tests/${linkId}/processing-status`);
      
      const data = response.data;
      
      if (data.status === 'processing') {
        setIsProcessing(true);
        setProcessingStatus(data.message || 'Processing your reading...');
        return false; // Still processing
      } else {
        setIsProcessing(false);
        return true; // Completed
      }
    } catch (error) {
      console.error('Error checking processing status:', error);
      // Fallback to debug-attempts endpoint
      try {
        const fallbackResponse = await api.get(`/api/tests/${linkId}/debug-attempts`);
        
        if (fallbackResponse.data.totalAttempts === 0) {
          setIsProcessing(true);
          setProcessingStatus('Analyzing your reading...');
          return false;
        } else {
          setIsProcessing(false);
          return true;
        }
      } catch (fallbackError) {
        console.error('Fallback status check failed:', fallbackError);
        return true; // Assume completed to avoid infinite loading
      }
    }
  };

  const fetchResult = async () => {
    try {
      // First get the test info to determine grading method
      const testRes = await api.get(`/api/tests/${linkId}`);
      
      const gradingMethod = testRes.data.gradingMethod || 'speechace';
      const endpoint = gradingMethod === 'whisper' ? 'whisper-result' : 'result';
      
      // Try to get the result first - if it exists, show it immediately
      try {
        const resultRes = await api.get(`/api/tests/${linkId}/${endpoint}`);
        
        // Success! Report exists and is ready
        setResult(resultRes.data);
        setLoading(false);
        setIsProcessing(false);
        return;
      } catch (resultError) {
        // Report doesn't exist yet - check if processing
        if (resultError.response?.status === 404 && gradingMethod === 'whisper') {
          const isCompleted = await checkProcessingStatus();
          if (!isCompleted) {
            // Still processing - start polling
            return;
          } else {
            // Should be completed but result not found - try once more
            try {
              const retryRes = await api.get(`/api/tests/${linkId}/${endpoint}`);
              setResult(retryRes.data);
              setLoading(false);
              setIsProcessing(false);
              return;
            } catch (retryError) {
              setError('Report not found');
              setLoading(false);
              return;
            }
          }
        } else {
          // Non-whisper test or other error
          setError('Report not found');
          setLoading(false);
          return;
        }
      }
    } catch (error) {
      console.error('Error fetching test info:', error);
      setError('Report not found');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResult();
  }, [linkId]);

  // Populate overridden words set when result is loaded
  useEffect(() => {
    if (result && result.sentenceAttempts) {
      const overridden = new Set();
      result.sentenceAttempts.forEach((attempt, sentenceIndex) => {
        if (attempt.wordAnalysis) {
          attempt.wordAnalysis.forEach((word, wordIndex) => {
            if (word.override && word.override.isOverridden) {
              overridden.add(`${sentenceIndex}-${wordIndex}`);
            }
          });
        }
      });
      setOverriddenWords(overridden);
    }
  }, [result]);

  // Polling for processing completion (only when actually processing)
  useEffect(() => {
    if (!isProcessing || result) return; // Don't poll if we already have a result

    const interval = setInterval(async () => {
      const isCompleted = await checkProcessingStatus();
      if (isCompleted) {
        setProcessingStatus('Finalizing report...');
        setTimeout(fetchResult, 1000); // Small delay before fetching result
      } else {
        // Update processing status with more specific messages
        const statusMessages = [
          'Processing audio with Whisper AI...',
          'Analyzing reading accuracy...',
          'Generating detailed feedback...',
          'Almost done, finalizing report...'
        ];
        const randomStatus = statusMessages[Math.floor(Math.random() * statusMessages.length)];
        setProcessingStatus(randomStatus);
      }
    }, 3000); // Check every 3 seconds

    return () => clearInterval(interval);
  }, [isProcessing, linkId, result]);

  // Remove the full-screen regeneration overlay - now handled inline

  // Only show processing screen for genuinely processing reports
  if (isProcessing && !result) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh" 
        bgcolor="#f0f4fa" 
        p={3}
      >
        <Paper 
          sx={{ 
            p: 6, 
            maxWidth: 500, 
            width: '100%', 
            borderRadius: 4, 
            textAlign: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            boxShadow: '0 20px 40px rgba(102, 126, 234, 0.3)'
          }}
        >
          <Box mb={4}>
            <CircularProgress 
              size={80} 
              thickness={4} 
              sx={{ 
                color: 'white',
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(1)', opacity: 1 },
                  '50%': { transform: 'scale(1.1)', opacity: 0.7 },
                  '100%': { transform: 'scale(1)', opacity: 1 }
                }
              }} 
            />
          </Box>
          
          <Typography variant="h4" fontWeight={700} mb={2}>
            🔄 Processing Your Report
          </Typography>
          
          <Typography variant="h6" mb={3} sx={{ opacity: 0.9 }}>
            {processingStatus || 'Please wait while we prepare your reading analysis...'}
          </Typography>
          
          <Box 
            sx={{
              background: 'rgba(255,255,255,0.1)',
              borderRadius: 2,
              p: 2,
              border: '1px solid rgba(255,255,255,0.2)'
            }}
          >
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              ⏱️ This usually takes 30-60 seconds<br />🎯 We're analyzing your reading with AI
            </Typography>
          </Box>
          
          <Box mt={3}>
            <Box 
              sx={{
                width: '100%',
                height: 6,
                bgcolor: 'rgba(255,255,255,0.2)',
                borderRadius: 3,
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, #4fc3f7, #29b6f6, #03a9f4, #0288d1)',
                  backgroundSize: '200% 100%',
                  animation: 'shimmer 2s infinite linear',
                  '@keyframes shimmer': {
                    '0%': { backgroundPosition: '-200% 0' },
                    '100%': { backgroundPosition: '200% 0' }
                  }
                }}
              />
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  }

  // Simple loading for initial fetch (no fancy processing screen)
  if (loading && !result) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={40} />
      </Box>
    );
  }
  if (error) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh"><Alert severity="error">{error}</Alert></Box>;
  if (!result) return null;

  const sentenceAttempts = result.sentenceAttempts || result.reportDetails?.sentenceAttempts || [];
  const gradingMethod = result.testId?.gradingMethod || 'speechace';
  const passageType = result.testId?.passageId?.passageType;
  const isStory = passageType === 'story';
  const unitLabel = isStory ? 'Paragraph' : 'Sentence';
  const unitLabelLower = isStory ? 'paragraph' : 'sentence';

  // Extract different types of errors from GPT analysis for better categorization
  const getErrorAnalysis = () => {
    const errors = {
      miscues: [],
      omittedWords: [],
      selfCorrections: [],
      lowScoreWords: []  // Words from wordAnalysis that are incorrect but not in GPT categories
    };

    sentenceAttempts.forEach((attempt, sentenceIndex) => {
      if (gradingMethod === 'whisper' && attempt.gptAnalysisResponse) {
        const gpt = attempt.gptAnalysisResponse;
        
        // Helper function to find actual word position in wordAnalysis
        const findWordPosition = (gptWord, wordAnalysis) => {
          if (!wordAnalysis || !gptWord) return -1;
          
          const normalizedGptWord = gptWord.toLowerCase().replace(/[,.]$/, '');
          
          // First priority: find by target word match (most reliable)
          const targetMatch = wordAnalysis.findIndex(w => 
            w.targetWord && w.targetWord.toLowerCase().replace(/[,.]$/, '') === normalizedGptWord
          );
          
          if (targetMatch !== -1) return targetMatch;
          
          // Second priority: find by transcribed word match (for self-corrections)
          const transcribedMatch = wordAnalysis.findIndex(w => 
            w.transcribedWord && w.transcribedWord.toLowerCase().replace(/[,.]$/, '') === normalizedGptWord
          );
          
          if (transcribedMatch !== -1) return transcribedMatch;
          
          // Final fallback: try partial matching without punctuation
          const partialMatch = wordAnalysis.findIndex(w => {
            const normalizedTarget = (w.targetWord || '').toLowerCase().replace(/[^\w]/g, '');
            const normalizedTranscribed = (w.transcribedWord || '').toLowerCase().replace(/[^\w]/g, '');
            const normalizedGpt = normalizedGptWord.replace(/[^\w]/g, '');
            return normalizedTarget === normalizedGpt || normalizedTranscribed === normalizedGpt;
          });
          
          return partialMatch !== -1 ? partialMatch : -1;
        };

        // Process miscues from GPT analysis
        if (gpt.miscues) {
          gpt.miscues.forEach((miscue, index) => {
            const actualWordIndex = findWordPosition(miscue.targetWord, attempt.wordAnalysis);
            
            errors.miscues.push({
              type: 'miscue',
              targetWord: miscue.targetWord,
              miscue: miscue.saidWord, // What student actually said from GPT
              accuracy: parseInt(miscue.score) || 0,
              reason: miscue.reason,
              sentenceIndex: sentenceIndex,
              wordIndex: actualWordIndex !== -1 ? actualWordIndex : index,
              audioKey: `${sentenceIndex}-${actualWordIndex !== -1 ? `miscue-${index}` : index}`,
              timing: miscue.timing || null,
              override: null
            });
          });
        }
        
        // Process omitted words from GPT analysis
        if (gpt.omittedWords) {
          gpt.omittedWords.forEach((omitted, index) => {
            const actualWordIndex = findWordPosition(omitted.targetWord, attempt.wordAnalysis);
            errors.omittedWords.push({
              type: 'omitted',
              targetWord: omitted.targetWord,
              miscue: 'Omitted',
              accuracy: parseInt(omitted.score) || 0,
              reason: omitted.reason,
              sentenceIndex: sentenceIndex,
              wordIndex: actualWordIndex !== -1 ? actualWordIndex : index,
              audioKey: `${sentenceIndex}-${actualWordIndex !== -1 ? `omitted-${index}` : index}`,
              timing: omitted.timing || null,
              override: null
            });
          });
        }
        
        // Process self corrections from GPT analysis
        if (gpt.selfCorrection) {
          gpt.selfCorrection.forEach((correction, index) => {
            const actualWordIndex = findWordPosition(correction.targetWord, attempt.wordAnalysis);
            
            errors.selfCorrections.push({
              type: 'self_correction',
              targetWord: correction.targetWord,
              miscue: correction.saidWord + ' (self-corrected)',
              accuracy: parseInt(correction.score) || 0,
              reason: correction.reason,
              sentenceIndex: sentenceIndex,
              wordIndex: actualWordIndex !== -1 ? actualWordIndex : index,
              audioKey: `${sentenceIndex}-${actualWordIndex !== -1 ? `correction-${index}` : index}`,
              timing: correction.timing || null,
              override: null
            });
          });
        }
      }
      
      // Also check wordAnalysis for any incorrect words not covered by GPT categories
      if (attempt.wordAnalysis) {
        attempt.wordAnalysis.forEach((word, wordIndex) => {
          let isWrong = false;
          let accuracy = 0;

          if (gradingMethod === 'whisper') {
            accuracy = getWordScore(word, sentenceIndex, wordIndex, gradingMethod, sentenceAttempts);
            isWrong = accuracy < 93.01;
          } else {
            isWrong = word.qualityScore < 93.01;
            accuracy = word.qualityScore;
          }

          // Check if word is overridden
          if (word.override && word.override.isOverridden) {
            if (word.override.overrideType === 'correct' || word.override.overrideType === 'accent') {
              // Override to mark as correct
              isWrong = false;
              accuracy = 100;
            } else if (word.override.overrideType === 'incorrect') {
              // Override to mark as incorrect
              isWrong = true;
              accuracy = 0;
            }
          }

          // Add to errors if wrong (both Whisper and SpeechAce)
          if (isWrong) {
            // For Whisper, check if already in GPT categories to avoid duplicates
            if (gradingMethod === 'whisper') {
              const normalizedTarget = (word.targetWord || '').toLowerCase().replace(/[,.]$/, '');
              const alreadyInGPT =
                errors.miscues.some(m => m.targetWord.toLowerCase().replace(/[,.]$/, '') === normalizedTarget) ||
                errors.omittedWords.some(o => o.targetWord.toLowerCase().replace(/[,.]$/, '') === normalizedTarget) ||
                errors.selfCorrections.some(s => s.targetWord.toLowerCase().replace(/[,.]$/, '') === normalizedTarget);

              // Only add if not already covered by GPT analysis
              if (!alreadyInGPT) {
                const miscue = determineMiscue(word);
                const reason = getMiscueReason(sentenceIndex, wordIndex, gradingMethod, sentenceAttempts, word.targetWord || word.word, word.transcribedWord);
                errors.lowScoreWords.push({
                  type: 'low_score',
                  targetWord: removePunctuation(word.targetWord || word.word),
                  miscue: miscue,
                  accuracy: accuracy,
                  reason: reason,
                  sentenceIndex: sentenceIndex,
                  wordIndex: wordIndex,
                  audioKey: `${sentenceIndex}-${wordIndex}`,
                  phoneScores: word.phoneScores || [],
                  override: word.override || null
                });
              }
            } else {
              // For SpeechAce, always add
              const miscue = determineMiscue(word);
              const reason = getMiscueReason(sentenceIndex, wordIndex, gradingMethod, sentenceAttempts, word.targetWord || word.word, word.transcribedWord);
              errors.lowScoreWords.push({
                type: 'low_score',
                targetWord: removePunctuation(word.targetWord || word.word),
                miscue: miscue,
                accuracy: accuracy,
                reason: reason,
                sentenceIndex: sentenceIndex,
                wordIndex: wordIndex,
                audioKey: `${sentenceIndex}-${wordIndex}`,
                phoneScores: word.phoneScores || [],
                override: word.override || null
              });
            }
          }
        });
      }
    });
    
    return errors;
  };

  // Legacy function for backward compatibility
  const getWrongWords = () => {
    const errors = getErrorAnalysis();
    return [...errors.miscues, ...errors.omittedWords, ...errors.selfCorrections, ...errors.lowScoreWords];
  };

  // Helper to calculate correct sentence accuracy
  const calculateSentenceAccuracy = (attempt) => {
    if (gradingMethod === 'whisper') {
      if (attempt.wordAnalysis && attempt.wordAnalysis.length > 0) {
        const correctWords = attempt.wordAnalysis.filter(word => {
          // Check for override first
          if (word.override && word.override.isOverridden) {
            if (word.override.overrideType === 'incorrect') {
              return false; // Explicitly marked as incorrect
            }
            return word.override.overrideType === 'correct' || word.override.overrideType === 'accent';
          }
          return word.isCorrect;
        }).length;
        const totalWords = attempt.wordAnalysis.length;
        return totalWords > 0 ? Math.round((correctWords / totalWords) * 100) : 0;
      }
      return 0;
    } else {
      return attempt.qualityScore;
    }
  };

  // Helper to recalculate overall accuracy across all sentences
  const calculateOverallAccuracy = (sentenceAttempts) => {
    if (!sentenceAttempts || sentenceAttempts.length === 0) return 0;

    let totalCorrect = 0;
    let totalWords = 0;

    sentenceAttempts.forEach((attempt) => {
      if (gradingMethod === 'whisper' && attempt.wordAnalysis) {
        attempt.wordAnalysis.forEach(word => {
          totalWords++;

          // Check for override first
          if (word.override && word.override.isOverridden) {
            if (word.override.overrideType === 'correct' || word.override.overrideType === 'accent') {
              totalCorrect++;
            }
            // 'incorrect' and 'correction' types are not counted as correct
          } else {
            // Use original scoring
            if (word.isCorrect) {
              totalCorrect++;
            }
          }
        });
      } else if (gradingMethod === 'speechace' && attempt.wordAnalysis) {
        // For SpeechAce, use quality scores
        attempt.wordAnalysis.forEach(word => {
          totalWords++;

          // Check for override first
          if (word.override && word.override.isOverridden) {
            if (word.override.overrideType === 'correct' || word.override.overrideType === 'accent') {
              totalCorrect++;
            }
          } else {
            if (word.qualityScore >= 93.01) {
              totalCorrect++;
            }
          }
        });
      }
    });

    return totalWords > 0 ? Math.round((totalCorrect / totalWords) * 100) : 0;
  };

  // Determine what the student likely said based on phoneme analysis
  const determineMiscue = (word) => {
    if (gradingMethod === 'whisper') {
      // For Whisper, show what was transcribed, cleaned up
      if (word.transcribedWord === '') {
        return 'Omitted';
      }
      return removePunctuation(word.transcribedWord);
    }
    
    // For SpeechAce, use phoneme analysis
    if (!word.phoneScores || word.phoneScores.length === 0) {
      return 'Unknown';
    }
    
    // Try to reconstruct what they said from sound_most_like phonemes
    const phoneticMiscue = word.phoneScores
      .map(phone => {
        if (phone.soundMostLike && phone.soundMostLike !== phone.phone) {
          return phone.soundMostLike;
        }
        return phone.phone;
      })
      .join('');
    
    // If all phonemes match original, show as "Mispronunciation"
    const allMatch = word.phoneScores.every(phone => 
      !phone.soundMostLike || phone.soundMostLike === phone.phone
    );
    
    if (allMatch) {
      return 'Mispronunciation';
    }
    
    // Return phonetic representation if different
    return phoneticMiscue !== word.phoneScores.map(p => p.phone).join('') 
      ? `/${phoneticMiscue}/` 
      : 'Mispronunciation';
  };

  // Audio playback functions
  const handlePlayAudio = async (audioKey, sentenceIndex, wordIndex) => {
    console.log('🎵 MAIN handlePlayAudio started', { audioKey, sentenceIndex, wordIndex });
    
    try {
      if (playingAudio === audioKey) {
        console.log('🔇 Pausing currently playing audio');
        // Pause currently playing audio
        if (audioElements[audioKey]) {
          audioElements[audioKey].pause();
        }
        setPlayingAudio(null);
        return;
      }

      // Stop any currently playing audio
      if (playingAudio && audioElements[playingAudio]) {
        console.log('🛑 Stopping other playing audio');
        audioElements[playingAudio].pause();
      }

      console.log('🌐 Fetching audio from backend...');
      // Request word-specific audio from backend using appropriate endpoint
      const audioEndpoint = gradingMethod === 'whisper' ? 'whisper-word-audio' : 'word-audio';
      const response = await api.get(
        `/api/tests/${linkId}/${audioEndpoint}/${sentenceIndex}/${wordIndex}`,
        { responseType: 'blob' }
      );

      console.log('🎧 Creating audio element...');
      const audioBlob = new Blob([response.data], { type: 'audio/wav' });
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      
      audio.onended = () => {
        console.log('🏁 Audio ended - cleaning up', { audioKey });
        setPlayingAudio(null);
        URL.revokeObjectURL(audioUrl);
        console.log('✅ Audio cleanup complete - buttons should still work');
      };
      
      audio.onerror = () => {
        console.log('❌ Audio error - cleaning up', { audioKey });
        setPlayingAudio(null);
        URL.revokeObjectURL(audioUrl);
      };

      console.log('📱 Updating audio state and playing...');
      setAudioElements(prev => ({ ...prev, [audioKey]: audio }));
      setPlayingAudio(audioKey);
      audio.play();
      
      console.log('🎵 Audio play started successfully');

    } catch (error) {
      console.error('Error playing word audio:', error);
      setPlayingAudio(null);
    }
  };

  // Sentence audio playback function (full paragraph/sentence)
  const handlePlaySentenceAudio = async (sentenceIndex) => {
    try {
      const audioKey = `sentence-${sentenceIndex}`;
      
      if (playingAudio === audioKey) {
        // Pause currently playing audio
        if (audioElements[audioKey]) {
          audioElements[audioKey].pause();
        }
        setPlayingAudio(null);
        return;
      }

      // Stop any currently playing audio
      if (playingAudio && audioElements[playingAudio]) {
        audioElements[playingAudio].pause();
      }

      // Request sentence-specific audio from backend
      const response = await api.get(
        `/api/tests/${linkId}/sentence-audio/${sentenceIndex}`,
        { responseType: 'blob' }
      );

      const audioBlob = new Blob([response.data], { type: 'audio/wav' });
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      
      audio.onended = () => {
        setPlayingAudio(null);
        URL.revokeObjectURL(audioUrl);
      };
      
      audio.onerror = () => {
        setPlayingAudio(null);
        URL.revokeObjectURL(audioUrl);
      };

      setAudioElements(prev => ({ ...prev, [audioKey]: audio }));
      setPlayingAudio(audioKey);
      audio.play();

    } catch (error) {
      console.error('Error playing sentence audio:', error);
      setPlayingAudio(null);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Override dialog functions
  const openOverrideDialog = (wordData) => {
    setOverrideDialog({ open: true, wordData });
    setOverrideType('');
    setCorrectedWord('');
    setOverrideReason('');
  };

  const closeOverrideDialog = () => {
    setOverrideDialog({ open: false, wordData: null });
    setOverrideType('');
    setCorrectedWord('');
    setOverrideReason('');
  };

  const handleOverrideSubmit = async () => {
    try {
      const { wordData } = overrideDialog;
      
      const response = await api.post(`/api/tests/${linkId}/override-word`, {
        sentenceIndex: wordData.sentenceIndex,
        wordIndex: wordData.wordIndex,
        overrideType: overrideType,
        correctedWord: overrideType === 'correction' ? correctedWord : null,
        reason: overrideReason
      });

      if (response.data.success) {
        // Update local state immediately - no page refresh!
        setResult(prevResult => {
          const newResult = { ...prevResult };
          if (newResult.sentenceAttempts && newResult.sentenceAttempts[wordData.sentenceIndex] && 
              newResult.sentenceAttempts[wordData.sentenceIndex].wordAnalysis &&
              newResult.sentenceAttempts[wordData.sentenceIndex].wordAnalysis[wordData.wordIndex]) {
            
            newResult.sentenceAttempts[wordData.sentenceIndex].wordAnalysis[wordData.wordIndex] = {
              ...newResult.sentenceAttempts[wordData.sentenceIndex].wordAnalysis[wordData.wordIndex],
              override: {
                isOverridden: true,
                overrideType: overrideType,
                correctedWord: overrideType === 'correction' ? correctedWord : null,
                reason: overrideReason,
                timestamp: new Date().toISOString()
              }
            };

            // Recalculate overall accuracy
            newResult.accuracy = calculateOverallAccuracy(newResult.sentenceAttempts);
          }
          return newResult;
        });

        // Add to overridden words set for highlighting
        setOverriddenWords(prev => new Set(prev).add(`${wordData.sentenceIndex}-${wordData.wordIndex}`));
        
        setSnackbar({ open: true, message: 'Override applied successfully! ✅', severity: 'success' });
        closeOverrideDialog();
      }
    } catch (error) {
      console.error('Error applying override:', error);
      setSnackbar({ open: true, message: 'Failed to apply override', severity: 'error' });
    }
  };

  const handleRemoveOverride = async (wordData) => {
    try {
      const response = await api.delete(`/api/tests/${linkId}/override-word`, {
        data: {
          sentenceIndex: wordData.sentenceIndex,
          wordIndex: wordData.wordIndex
        }
      });

      if (response.data.success) {
        // Update local state immediately - no page refresh!
        setResult(prevResult => {
          const newResult = { ...prevResult };
          if (newResult.sentenceAttempts && newResult.sentenceAttempts[wordData.sentenceIndex] && 
              newResult.sentenceAttempts[wordData.sentenceIndex].wordAnalysis &&
              newResult.sentenceAttempts[wordData.sentenceIndex].wordAnalysis[wordData.wordIndex]) {
            
            // Remove the override property
            const word = newResult.sentenceAttempts[wordData.sentenceIndex].wordAnalysis[wordData.wordIndex];
            delete word.override;

            // Recalculate overall accuracy
            newResult.accuracy = calculateOverallAccuracy(newResult.sentenceAttempts);
          }
          return newResult;
        });

        // Remove from overridden words set
        setOverriddenWords(prev => {
          const newSet = new Set(prev);
          newSet.delete(`${wordData.sentenceIndex}-${wordData.wordIndex}`);
          return newSet;
        });
        
        setSnackbar({ open: true, message: 'Override removed successfully! ✅', severity: 'success' });
      }
    } catch (error) {
      console.error('Error removing override:', error);
      setSnackbar({ open: true, message: 'Failed to remove override', severity: 'error' });
    }
  };

  // Regeneration handlers
  const openRegenerationDialog = (sentenceData) => {
    setRegenerationDialog({ open: true, sentenceData });
  };

  const closeRegenerationDialog = () => {
    setRegenerationDialog({ open: false, sentenceData: null });
  };

  const handleRegenerateSentence = async (sentenceIndex) => {
    try {
      setRegeneratingSentence(sentenceIndex);
      setRegenerationStatus('Starting regeneration...');
      
      const response = await api.post(`/api/tests/${linkId}/regenerate-sentence`, {
        sentenceIndex
      });

      if (response.data.success) {
        setRegenerationStatus('Transcribing audio with Whisper AI...');
        
        // Start polling for regeneration completion
        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await api.get(`/api/tests/${linkId}/regeneration-status/${sentenceIndex}`);
            const { status, stage, message } = statusResponse.data;
            
            setRegenerationStatus(message || `Processing: ${stage}`);
            
            if (status === 'completed') {
              clearInterval(pollInterval);
              
              // Refresh the test data to show new results
              await fetchResult();
              
              const currentPassageType = result?.testId?.passageId?.passageType;
              
              // Mark sentence as recently regenerated
              setRecentlyRegeneratedSentences(prev => new Set(prev).add(sentenceIndex));
              
              // Clear regeneration state
              setRegeneratingSentence(null);
              setRegenerationStatus('');
              
              setSnackbar({ 
                open: true, 
                message: `Successfully regenerated ${currentPassageType === 'story' ? 'paragraph' : 'sentence'} ${sentenceIndex + 1}! 🎉`, 
                severity: 'success' 
              });
              
              // Remove the "recently regenerated" indicator after 10 seconds
              setTimeout(() => {
                setRecentlyRegeneratedSentences(prev => {
                  const newSet = new Set(prev);
                  newSet.delete(sentenceIndex);
                  return newSet;
                });
              }, 10000);
            } else if (status === 'failed') {
              clearInterval(pollInterval);
              setRegeneratingSentence(null);
              setRegenerationStatus('');
              setSnackbar({ 
                open: true, 
                message: 'Regeneration failed. Please try again.', 
                severity: 'error' 
              });
            }
          } catch (pollError) {
            console.error('Error polling regeneration status:', pollError);
            
            // If it's a 500 error, the regeneration might have completed but status was cleaned up
            // Let's check if the data was actually updated by refreshing
            if (pollError.response?.status === 500) {
              console.log('🔄 Status polling failed, checking if regeneration completed...');
              try {
                // Try to refresh the result to see if regeneration actually completed
                await fetchResult();
                
                // If we successfully fetched new data, assume regeneration completed
                clearInterval(pollInterval);
                setRecentlyRegeneratedSentences(prev => new Set(prev).add(sentenceIndex));
                setRegeneratingSentence(null);
                setRegenerationStatus('');
                setSnackbar({ 
                  open: true, 
                  message: `Successfully regenerated ${result?.testId?.passageId?.passageType === 'story' ? 'paragraph' : 'sentence'} ${sentenceIndex + 1}! 🎉`, 
                  severity: 'success' 
                });
                
                // Remove indicator after 10 seconds
                setTimeout(() => {
                  setRecentlyRegeneratedSentences(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(sentenceIndex);
                    return newSet;
                  });
                }, 10000);
                
                return;
              } catch (refreshError) {
                console.error('Failed to check completion by refreshing:', refreshError);
              }
            }
            
            // If we can't determine completion, show error
            clearInterval(pollInterval);
            setRegeneratingSentence(null);
            setRegenerationStatus('');
            setSnackbar({ 
              open: true, 
              message: 'Error checking regeneration status. Please refresh the page to see if regeneration completed.', 
              severity: 'warning' 
            });
          }
        }, 3000); // Poll every 3 seconds
        
        // Timeout after 5 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          if (regeneratingSentence === sentenceIndex) {
            setRegeneratingSentence(null);
            setRegenerationStatus('');
            setSnackbar({ 
              open: true, 
              message: 'Regeneration timeout. Please try again.', 
              severity: 'error' 
            });
          }
        }, 300000); // 5 minutes
      }
    } catch (error) {
      console.error('Error starting regeneration:', error);
      setRegeneratingSentence(null);
      setRegenerationStatus('');
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.error || 'Failed to start regeneration', 
        severity: 'error' 
      });
    }
  };

  // Quick override handler for enhanced tooltips
  const handleQuickOverride = async (overrideData) => {
    console.log('🔥 HANDLEQUICKOVERRIDE STARTED!', {
      timestamp: Date.now(),
      overrideData,
      stackTrace: new Error().stack?.split('\n')[1]
    });
    
    const { sentenceIndex, wordIndex, targetWord, overrideType, showDialog, removeOverride } = overrideData;
    
    console.log('📝 handleQuickOverride processing:', {
      sentenceIndex, 
      wordIndex, 
      targetWord, 
      overrideType,
      currentPlayingAudio: playingAudio,
      showDialog,
      removeOverride
    });
    
    // Stop any currently playing audio to avoid conflicts - but don't await
    if (playingAudio && audioElements[playingAudio]) {
      console.log('🔇 Stopping audio before override:', playingAudio);
      try {
        audioElements[playingAudio].pause();
        audioElements[playingAudio].currentTime = 0;
      } catch (e) {
        console.warn('Audio cleanup failed:', e);
      }
      setPlayingAudio(null);
    }
    
    if (removeOverride) {
      return handleRemoveOverride({ sentenceIndex, wordIndex });
    }
    
    if (showDialog) {
      openOverrideDialog({
        sentenceIndex,
        wordIndex,
        targetWord,
        miscue: 'Custom Override',
        accuracy: 0
      });
      return;
    }

    try {
      console.log('🚀 Sending override request:', {
        linkId,
        sentenceIndex,
        wordIndex,
        overrideType
      });
      
      const response = await api.post(`/api/tests/${linkId}/override-word`, {
        sentenceIndex,
        wordIndex,
        overrideType,
        correctedWord: null,
        reason: overrideType === 'correct' ? 'Teacher marked as correctly read' :
                 overrideType === 'accent' ? 'Acceptable accent/dialect variant' :
                 overrideType === 'incorrect' ? 'Teacher marked as incorrectly read' : ''
      });

      console.log('✅ Override response:', response.data);

      if (response.data.success) {
        console.log('🔄 Updating local state for word:', { sentenceIndex, wordIndex });
        
        // Update local state immediately - no page refresh!
        setResult(prevResult => {
          const newResult = { ...prevResult };
          console.log('📋 Before update - checking path:', {
            hasSentenceAttempts: !!newResult.sentenceAttempts,
            hasSentence: !!(newResult.sentenceAttempts && newResult.sentenceAttempts[sentenceIndex]),
            hasWordAnalysis: !!(newResult.sentenceAttempts && newResult.sentenceAttempts[sentenceIndex] && newResult.sentenceAttempts[sentenceIndex].wordAnalysis),
            hasWord: !!(newResult.sentenceAttempts && newResult.sentenceAttempts[sentenceIndex] && newResult.sentenceAttempts[sentenceIndex].wordAnalysis && newResult.sentenceAttempts[sentenceIndex].wordAnalysis[wordIndex])
          });
          
          if (newResult.sentenceAttempts && newResult.sentenceAttempts[sentenceIndex] && 
              newResult.sentenceAttempts[sentenceIndex].wordAnalysis &&
              newResult.sentenceAttempts[sentenceIndex].wordAnalysis[wordIndex]) {
            
            console.log('✍️ Updating word at path:', { sentenceIndex, wordIndex });
            
            newResult.sentenceAttempts[sentenceIndex].wordAnalysis[wordIndex] = {
              ...newResult.sentenceAttempts[sentenceIndex].wordAnalysis[wordIndex],
              override: {
                isOverridden: true,
                overrideType: overrideType,
                reason: overrideType === 'correct' ? 'Teacher marked as correctly read' :
                       overrideType === 'accent' ? 'Acceptable accent/dialect variant' :
                       overrideType === 'incorrect' ? 'Teacher marked as incorrectly read' : '',
                timestamp: new Date().toISOString()
              }
            };

            // Recalculate overall accuracy
            newResult.accuracy = calculateOverallAccuracy(newResult.sentenceAttempts);

            console.log('✅ Word updated successfully, new overall accuracy:', newResult.accuracy);
          } else {
            console.error('❌ Could not find word at path:', { sentenceIndex, wordIndex });
          }
          return newResult;
        });

        // Add to overridden words set for highlighting
        setOverriddenWords(prev => new Set(prev).add(`${sentenceIndex}-${wordIndex}`));
        
        const message = overrideType === 'correct' ? 'Word marked as correct! ✅' :
                        overrideType === 'accent' ? 'Word marked as acceptable accent! ✅' :
                        overrideType === 'incorrect' ? 'Word marked as incorrect! ❌' :
                        'Override applied! ✅';

        setSnackbar({
          open: true,
          message,
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error applying quick override:', error);
      setSnackbar({ open: true, message: 'Failed to apply override', severity: 'error' });
    }
  };

  const wrongWords = getWrongWords();

  return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh" bgcolor="#f0f4fa" p={3}>
      <Paper sx={{ p: 4, maxWidth: 1000, width: '100%', borderRadius: 3, mb:3 }}>
        {/* Header */}
        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box flexGrow={1} textAlign="center">
              <Typography variant="h4" mb={2}>Test Report</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Tooltip title="Download Full Audio (MP3)">
                <IconButton 
                  onClick={() => window.open(`${process.env.REACT_APP_API_URL}/api/tests/${linkId}/full-audio`, '_blank')}
                  sx={{ backgroundColor: '#f5f5f5', '&:hover': { backgroundColor: '#e0e0e0' } }}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Back to Dashboard">
                <IconButton 
                  onClick={() => navigate('/dashboard', { state: { defaultTab: 1 } })}
                  sx={{ backgroundColor: '#f5f5f5', '&:hover': { backgroundColor: '#e0e0e0' } }}
                >
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" mb={1}>Student: {result.studentName}</Typography>
            <Typography variant="h6" mb={1}>Passage: {result.testId?.passageId?.title || 'N/A'}</Typography>
            <Box display="flex" justifyContent="center" mb={2}>
              <Chip 
                label={`Graded by: ${gradingMethod === 'whisper' ? 'Whisper AI' : 'SpeechAce'}`}
                color={gradingMethod === 'whisper' ? 'secondary' : 'primary'}
                sx={{ fontWeight: 'bold' }}
              />
            </Box>
          </Box>
          
          <Grid container spacing={2} justifyContent="center" mb={3}>
              <Grid item xs={12} sm={4}><Typography fontSize={20}>Overall Accuracy: <b>{result.accuracy}%</b></Typography></Grid>
              <Grid item xs={12} sm={4}><Typography fontSize={20}>Reading Time: <b>{formatReadingTime(result.readingTime)}</b></Typography></Grid>
              <Grid item xs={12} sm={4}><Typography fontSize={20}>Words Per Minute: <b>{result.wordsPerMinute || 'N/A'}</b></Typography></Grid>
          </Grid>
        </Box>

        {/* Regeneration Status Banner */}
        {regeneratingSentence !== null && (
          <Alert 
            severity="info" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 152, 0, 0.05) 100%)',
              border: '1px solid rgba(255, 152, 0, 0.3)'
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <AutorenewIcon sx={{ 
                animation: 'spin 1s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }} />
              <Typography variant="body2" fontWeight={600}>
                Regenerating {unitLabelLower} {regeneratingSentence + 1}...
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {regenerationStatus || 'Re-analyzing with Whisper AI and OpenAI. You can continue viewing other parts of the report.'}
            </Typography>
          </Alert>
        )}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} centered>
            <Tab label="Detailed Analysis" />
            <Tab label={`Error Analysis (${wrongWords.length})`} />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        {tabValue === 0 && sentenceAttempts.length > 0 && (
          <Box textAlign="left">
            <Typography variant="h5" mb={2} fontWeight={700}>Detailed {unitLabel} Analysis:</Typography>
            
            {/* Color Legend */}
            <Box mb={3} p={2} sx={{ backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <Typography variant="h6" mb={1} fontWeight={600}>Color Coding Guide:</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>Word Accuracy:</Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                    <Chip label="Excellent (93.01-100%)" sx={{ backgroundColor: '#e8f5e8', color: '#4caf50', fontWeight: 600 }} />
                    <Chip label="Good (80-93%)" sx={{ backgroundColor: '#fff3e0', color: '#ff9800', fontWeight: 600 }} />
                    <Chip label="Needs Practice (0-79.99%)" sx={{ backgroundColor: '#ffebee', color: '#f44336', fontWeight: 600 }} />
                    <Chip label="Teacher Override" sx={{ backgroundColor: '#e8f5e8', color: '#2e7d32', fontWeight: 600, border: '2px solid #4caf50' }} />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>{unitLabel} Accuracy:</Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                    <Chip label="Excellent (95%+)" sx={{ backgroundColor: '#e8f5e8', color: '#4caf50', fontWeight: 600 }} />
                    <Chip label="Good (80-94.99%)" sx={{ backgroundColor: '#fff3e0', color: '#ff9800', fontWeight: 600 }} />
                    <Chip label="Needs Practice (0-79.99%)" sx={{ backgroundColor: '#ffebee', color: '#f44336', fontWeight: 600 }} />
                  </Box>
                </Grid>
              </Grid>
              <Typography variant="caption" color="textSecondary" mt={1} display="block">
                * Only words with 93.01% or higher accuracy are considered correct
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block">
                * Words with green borders indicate teacher overrides
              </Typography>
            </Box>
            {sentenceAttempts.map((attempt, index) => {
              const isCurrentlyRegenerating = regeneratingSentence === attempt.sentenceIndex;
              const wasRecentlyRegenerated = recentlyRegeneratedSentences.has(attempt.sentenceIndex);
              
              return (
              <Accordion 
                key={index}
                disabled={isCurrentlyRegenerating} // Disable expansion while regenerating
                sx={{ 
                  mb: 2, 
                  borderRadius: 2, 
                  '&:before': { display: 'none' },
                  // Special styling for recently regenerated sentences
                  ...(wasRecentlyRegenerated && {
                    border: '2px solid #4caf50',
                    boxShadow: '0 4px 20px rgba(76, 175, 80, 0.2)',
                    background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, rgba(76, 175, 80, 0.02) 100%)'
                  }),
                  // Dimmed styling for sentences being regenerated
                  ...(isCurrentlyRegenerating && {
                    opacity: 0.7,
                    border: '2px solid #ff9800',
                    boxShadow: '0 4px 20px rgba(255, 152, 0, 0.2)',
                    background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.05) 0%, rgba(255, 152, 0, 0.02) 100%)'
                  })
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ 
                    background: getSentenceScoreBackground(calculateSentenceAccuracy(attempt)),
                    borderRadius: 2,
                    '&.Mui-expanded': { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }
                  }}
                >
                  <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography variant="subtitle1" fontWeight="bold" color={getSentenceScoreColor(calculateSentenceAccuracy(attempt))}>
                          {unitLabel} {attempt.sentenceIndex + 1}: {calculateSentenceAccuracy(attempt)}%
                        </Typography>
                        {wasRecentlyRegenerated && (
                          <Chip 
                            label="Recently Regenerated" 
                            size="small"
                            sx={{ 
                              backgroundColor: '#4caf50', 
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 22,
                              animation: 'pulse 2s infinite',
                              '@keyframes pulse': {
                                '0%': { transform: 'scale(1)' },
                                '50%': { transform: 'scale(1.05)' },
                                '100%': { transform: 'scale(1)' }
                              }
                            }}
                          />
                        )}
                        {isCurrentlyRegenerating && (
                          <Chip 
                            label="Regenerating..." 
                            size="small"
                            icon={<AutorenewIcon sx={{ 
                              fontSize: '16px !important',
                              animation: 'spin 1s linear infinite',
                              '@keyframes spin': {
                                '0%': { transform: 'rotate(0deg)' },
                                '100%': { transform: 'rotate(360deg)' }
                              }
                            }} />}
                            sx={{ 
                              backgroundColor: '#ff9800', 
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 22
                            }}
                          />
                        )}
                      </Box>
                      <Typography variant="body1" sx={{ 
                        fontStyle: 'italic', 
                        color: isCurrentlyRegenerating ? '#999' : '#666'
                      }}>
                        "{attempt.sentenceText}"
                      </Typography>
                      {gradingMethod === 'whisper' && (
                        <Typography variant="body2" sx={{ 
                          fontStyle: 'italic', 
                          color: isCurrentlyRegenerating ? '#bbb' : '#9C27B0', 
                          mt: 1 
                        }}>
                          Transcribed: "{attempt.transcribedText}"
                        </Typography>
                      )}
                      {isCurrentlyRegenerating && (
                        <Typography variant="body2" sx={{ 
                          color: '#ff9800', 
                          fontWeight: 600, 
                          mt: 1 
                        }}>
                          {regenerationStatus || 'Re-analyzing with Whisper AI and OpenAI...'}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent accordion from expanding
                          handlePlaySentenceAudio(attempt.sentenceIndex);
                        }}
                        sx={{ 
                          backgroundColor: playingAudio === `sentence-${attempt.sentenceIndex}` ? '#e3f2fd' : 'transparent',
                          '&:hover': { backgroundColor: '#e3f2fd' }
                        }}
                      >
                        {playingAudio === `sentence-${attempt.sentenceIndex}` ? <PauseIcon /> : <PlayArrowIcon />}
                      </IconButton>
                      <Typography variant="caption" sx={{ color: 'textSecondary', mr: 1 }}>
                        Listen
                      </Typography>
                      
                      {/* Regeneration Button */}
                      {gradingMethod === 'whisper' && (
                        <Tooltip title={
                          isCurrentlyRegenerating 
                            ? 'This sentence is currently being regenerated' 
                            : regeneratingSentence !== null 
                            ? 'Another sentence is being regenerated' 
                            : `Regenerate analysis for this ${unitLabelLower} using Whisper AI`
                        }>
                          <Box>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openRegenerationDialog({
                                  sentenceIndex: attempt.sentenceIndex,
                                  sentenceText: attempt.sentenceText,
                                  transcribedText: attempt.transcribedText
                                });
                              }}
                              disabled={regeneratingSentence !== null} // Disable all regeneration while any sentence is regenerating
                              sx={{ 
                                backgroundColor: isCurrentlyRegenerating ? '#fff3e0' : 'transparent',
                                '&:hover': { backgroundColor: isCurrentlyRegenerating ? '#fff3e0' : '#f5f5f5' },
                                '&:disabled': { 
                                  opacity: isCurrentlyRegenerating ? 0.9 : 0.5,
                                  backgroundColor: isCurrentlyRegenerating ? '#fff3e0' : 'transparent'
                                }
                              }}
                            >
                              {isCurrentlyRegenerating ? 
                                <AutorenewIcon sx={{ 
                                  animation: 'spin 1s linear infinite', 
                                  '@keyframes spin': { 
                                    '0%': { transform: 'rotate(0deg)' }, 
                                    '100%': { transform: 'rotate(360deg)' } 
                                  } 
                                }} /> : 
                                <RefreshIcon />
                              }
                            </IconButton>
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 2 }}>
                  {/* Overall Scores */}
                  {attempt.overallScores && (
                    <Box mb={3}>
                      <Typography variant="h6" fontWeight={600} mb={1}>Overall Pronunciation Scores:</Typography>
                      <Grid container spacing={1}>
                        {attempt.overallScores.speechace && (
                          <Grid item>
                            <Chip label={`SpeechAce: ${attempt.overallScores.speechace}`} color="primary" variant="outlined" />
                          </Grid>
                        )}
                        {attempt.overallScores.ielts && (
                          <Grid item>
                            <Chip label={`IELTS: ${attempt.overallScores.ielts}/9`} color="secondary" variant="outlined" />
                          </Grid>
                        )}
                        {attempt.overallScores.cefr && (
                          <Grid item>
                            <Chip label={`CEFR: ${attempt.overallScores.cefr}`} color="info" variant="outlined" />
                          </Grid>
                        )}
                        {attempt.overallScores.pte && (
                          <Grid item>
                            <Chip label={`PTE: ${attempt.overallScores.pte}/90`} color="success" variant="outlined" />
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  )}

                  {/* Quick Summary for Whisper */}
                  {gradingMethod === 'whisper' && attempt.wordAnalysis && (
                    <Box mb={3}>
                      <Typography variant="h6" fontWeight={600} mb={2}>Quick Summary:</Typography>
                      <Grid container spacing={1}>
                        {(() => {
                          const correctCount = attempt.wordAnalysis.filter(w => w.isCorrect || (w.override?.isOverridden && (w.override.overrideType === 'correct' || w.override.overrideType === 'accent'))).length;
                          const omittedCount = attempt.wordAnalysis.filter(w => w.transcribedWord === '').length;
                          const incorrectCount = attempt.wordAnalysis.filter(w => !w.isCorrect && w.transcribedWord !== '' && !(w.override?.isOverridden && (w.override.overrideType === 'correct' || w.override.overrideType === 'accent'))).length;
                          
                          return (
                            <>
                              <Grid item>
                                <Chip label={`✓ ${correctCount} Correct`} sx={{ backgroundColor: '#e8f5e8', color: '#2e7d32' }} />
                              </Grid>
                              {incorrectCount > 0 && (
                                <Grid item>
                                  <Chip label={`⚠ ${incorrectCount} Incorrect`} sx={{ backgroundColor: '#fff3e0', color: '#ff9800' }} />
                                </Grid>
                              )}
                              {omittedCount > 0 && (
                                <Grid item>
                                  <Chip label={`➖ ${omittedCount} Omitted`} sx={{ backgroundColor: '#ffebee', color: '#f44336' }} />
                                </Grid>
                              )}
                            </>
                          );
                        })()}
                      </Grid>
                      {(() => {
                        const omittedWords = attempt.wordAnalysis.filter(w => w.transcribedWord === '').map(w => w.targetWord.replace(/[,.]$/, ''));
                        if (omittedWords.length > 0) {
                          return (
                            <Box mt={2} p={2} sx={{ backgroundColor: '#ffebee', borderRadius: 1, border: '1px solid #ffcdd2' }}>
                              <Typography variant="body2" fontWeight={600} color="#d32f2f" mb={1}>
                                ➖ Omitted Words:
                              </Typography>
                              <Typography variant="body2" color="#d32f2f">
                                {omittedWords.join(', ')}
                              </Typography>
                            </Box>
                          );
                        }
                        return null;
                      })()}
                    </Box>
                  )}

                  {/* Sentence Breakdown for Long Paragraphs */}
                  {attempt.sentenceText.length > 100 && splitIntoSentences(attempt.sentenceText).length > 1 && (
                    <Box mb={3}>
                      <Typography variant="h5" fontWeight={700} mb={3} color="primary.main">Sentence-by-Sentence Breakdown:</Typography>
                      {getSentenceBoundariesFromTiming(attempt.wordAnalysis, attempt.sentenceText, attempt.transcribedText).map((sentenceBoundary, sentIdx) => (
                        <Box 
                          key={sentIdx} 
                          mb={3} 
                          p={3} 
                          sx={{ 
                            backgroundColor: '#f8f9fa', 
                            borderRadius: 3, 
                            border: '1px solid #e0e0e0',
                            position: 'relative',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                          }}
                        >
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="h6" fontWeight={700} color="#333" sx={{ fontSize: '1.1rem' }}>
                              Sentence {sentIdx + 1}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                {sentenceBoundary.words.length > 0 ? 
                                  Math.round(sentenceBoundary.words.filter(w => {
                                    if (w.override?.isOverridden) {
                                      return w.override.overrideType === 'correct' || w.override.overrideType === 'accent';
                                    }
                                    return gradingMethod === 'whisper' ? w.isCorrect : (w.qualityScore >= 93.01);
                                  }).length / sentenceBoundary.words.length * 100) : 0
                                }% Accuracy
                              </Typography>

                            </Box>
                          </Box>
                          
                          <Typography variant="body1" sx={{ fontStyle: 'italic', color: '#333', mb: 1, fontSize: '1rem', lineHeight: 1.5 }}>
                            "{sentenceBoundary.text}"
                          </Typography>
                          
                          {gradingMethod === 'whisper' && (
                            <Typography variant="body2" sx={{ color: '#9C27B0', fontStyle: 'italic', display: 'block', mb: 2, fontSize: '0.95rem' }}>
                              Transcribed: "{sentenceBoundary.transcribedText}"
                            </Typography>
                          )}
                          
                          <Grid container spacing={1}>
                            {sentenceBoundary.words.map((word, wordIdx) => {
                              const displayWord = gradingMethod === 'whisper' ? removePunctuation(word.targetWord) : removePunctuation(word.word);
                              const qualityScore = getWordScore(word, index, word.originalWordIndex !== undefined ? word.originalWordIndex : wordIdx, gradingMethod, sentenceAttempts);
                              const isOmitted = gradingMethod === 'whisper' && word.transcribedWord === '';
                              const isOverridden = word.override?.isOverridden;
                              
                              const getWordStyling = () => {
                                if (isOverridden) {
                                  if (word.override.overrideType === 'incorrect') {
                                    return {
                                      backgroundColor: '#ffebee',
                                      color: '#f44336',
                                      border: '2px solid #f44336'
                                    };
                                  } else {
                                    return {
                                      backgroundColor: '#e8f5e8',
                                      color: '#2e7d32',
                                      border: '2px solid #4caf50'
                                    };
                                  }
                                } else if (isOmitted) {
                                  return {
                                    backgroundColor: '#ffebee',
                                    color: '#d32f2f',
                                    border: '2px dashed #f44336',
                                    textDecoration: 'line-through'
                                  };
                                } else {
                                  return {
                                    backgroundColor: getScoreBackground(qualityScore),
                                    color: getScoreColor(qualityScore),
                                    border: 'none'
                                  };
                                }
                              };

                              return (
                                <Grid item key={wordIdx}>
                                  <EnhancedWordTooltip
                                    word={word}
                                    displayWord={displayWord}
                                    qualityScore={qualityScore}
                                    gradingMethod={gradingMethod}
                                    isOmitted={isOmitted}
                                    isOverridden={isOverridden}
                                    sentenceIndex={index}
                                    wordIndex={word.originalWordIndex !== undefined ? word.originalWordIndex : wordIdx}
                                    linkId={linkId}
                                    playingAudio={playingAudio}
                                    onPlayAudio={handlePlayAudio}
                                    onQuickOverride={handleQuickOverride}
                                  >
                                    <Chip 
                                      label={isOmitted ? `[${displayWord}]` : displayWord}
                                      icon={isOmitted ? <span style={{ fontSize: '18px' }}>➖</span> : null}
                                      sx={{ 
                                        ...getWordStyling(),
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        opacity: isOmitted ? 0.7 : 1,
                                        fontSize: '0.875rem',
                                        height: 32,
                                        transition: 'all 0.2s ease-in-out',
                                        '& .MuiChip-label': {
                                          fontSize: '0.875rem',
                                          fontWeight: 600
                                        },
                                        '&:hover': {
                                          opacity: 1,
                                          transform: 'scale(1.05)',
                                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                        }
                                      }}
                                    />
                                  </EnhancedWordTooltip>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Word-by-Word Analysis - only show for short sentences/single sentences */}
                  {attempt.wordAnalysis && attempt.wordAnalysis.length > 0 && 
                   !(attempt.sentenceText.length > 100 && splitIntoSentences(attempt.sentenceText).length > 1) && (
                    <Box>
                      <Typography variant="h6" fontWeight={600} mb={2}>Word-by-Word Analysis:</Typography>
                      <Grid container spacing={1}>
                        {attempt.wordAnalysis.map((word, wordIndex) => {
                          // Handle both SpeechAce and Whisper data structures
                          const displayWord = gradingMethod === 'whisper' ? removePunctuation(word.targetWord) : removePunctuation(word.word);
                          const qualityScore = getWordScore(word, index, wordIndex, gradingMethod, sentenceAttempts);
                          const isOmitted = gradingMethod === 'whisper' && word.transcribedWord === '';
                          const isOverridden = word.override?.isOverridden;
                          
                          // Determine the visual styling based on word status
                          const getWordStyling = () => {
                            if (isOverridden) {
                              // Different styling based on override type
                              if (word.override.overrideType === 'incorrect') {
                                // Red styling for incorrect overrides
                                return {
                                  backgroundColor: '#ffebee',
                                  color: '#f44336',
                                  border: '2px solid #f44336'
                                };
                              } else {
                                // Green styling for correct/accent/correction overrides
                                return {
                                  backgroundColor: '#e8f5e8',
                                  color: '#2e7d32',
                                  border: '2px solid #4caf50'
                                };
                              }
                            } else if (isOmitted) {
                              return {
                                backgroundColor: '#ffebee',
                                color: '#d32f2f',
                                border: '2px dashed #f44336',
                                textDecoration: 'line-through'
                              };
                            } else {
                              return {
                                backgroundColor: getScoreBackground(qualityScore),
                                color: getScoreColor(qualityScore),
                                border: 'none'
                              };
                            }
                          };

                          const wordStyling = getWordStyling();
                          
                          return (
                            <Grid item key={wordIndex}>
                              <EnhancedWordTooltip
                                word={word}
                                displayWord={displayWord}
                                qualityScore={qualityScore}
                                gradingMethod={gradingMethod}
                                isOmitted={isOmitted}
                                isOverridden={isOverridden}
                                sentenceIndex={index}
                                wordIndex={wordIndex}
                                linkId={linkId}
                                playingAudio={playingAudio}
                                onPlayAudio={handlePlayAudio}
                                onQuickOverride={handleQuickOverride}
                              >
                                <Chip 
                                  label={isOmitted ? `[${displayWord}]` : displayWord}
                                  icon={isOmitted ? <span style={{ fontSize: '16px' }}>➖</span> : null}
                                  sx={{ 
                                    ...wordStyling,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    opacity: isOmitted ? 0.7 : 1,
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                      opacity: 1,
                                      transform: 'scale(1.05)',
                                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                    }
                                  }}
                                />
                              </EnhancedWordTooltip>
                            </Grid>
                          );
                        })}
                      </Grid>
                      
                      {/* Legend */}
                      <Box mt={2} p={2} sx={{ backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                        <Typography variant="caption" color="textSecondary" mb={1} display="block" fontWeight={600}>
                          Word Analysis Legend:
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={3}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip label="word" size="small" sx={{ backgroundColor: '#e8f5e8', color: '#2e7d32' }} />
                              <Typography variant="caption">Correct</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip label="word" size="small" sx={{ backgroundColor: '#fff3e0', color: '#ff9800' }} />
                              <Typography variant="caption">Good</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip label="word" size="small" sx={{ backgroundColor: '#ffebee', color: '#f44336' }} />
                              <Typography variant="caption">Needs Practice</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip label="[word]" size="small" icon={<span style={{ fontSize: '12px' }}>➖</span>} 
                                sx={{ backgroundColor: '#ffebee', color: '#d32f2f', border: '1px dashed #f44336' }} />
                              <Typography variant="caption">Omitted</Typography>
                            </Box>
                          </Grid>
                        </Grid>
                        <Typography variant="caption" color="textSecondary" mt={1} display="block">
                          Hover over words to see detailed pronunciation feedback
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Fallback for legacy data without detailed analysis */}
                  {(!attempt.wordAnalysis || attempt.wordAnalysis.length === 0) && (
                    <Typography variant="body2" color="textSecondary">
                      Detailed word analysis not available for this {unitLabelLower}.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
              );
            })}
          </Box>
        )}

        {/* Error Analysis Tab */}
        {tabValue === 1 && (
          <Box>
            <Typography variant="h5" mb={3} fontWeight={700}>Error Analysis</Typography>
            
            {(() => {
              const errorAnalysis = getErrorAnalysis();
              const totalErrors = errorAnalysis.miscues.length + errorAnalysis.omittedWords.length + 
                                  errorAnalysis.selfCorrections.length + errorAnalysis.lowScoreWords.length;
              
              if (totalErrors === 0) {
                return (
                  <Box textAlign="center" py={4}>
                    <Typography variant="h6" color="success.main" mb={2}>🎉 Excellent Reading!</Typography>
                    <Typography variant="body1" color="textSecondary">
                      No errors detected. All words were read with excellent accuracy!
                    </Typography>
                  </Box>
                );
              }

              const ErrorSection = ({ title, items, color, icon, description }) => {
                if (items.length === 0) return null;
                
                return (
                  <Box mb={4}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Box sx={{ color, mr: 1, fontSize: '1.5rem' }}>{icon}</Box>
                      <Typography variant="h6" fontWeight={600} color={color}>
                        {title} ({items.length})
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary" mb={2}>
                      {description}
                    </Typography>
                    
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: color + '10' }}>
                            <TableCell sx={{ fontWeight: 600 }}>Target Word</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>What Happened</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Score</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {items.map((item, index) => {
                            const isWordOverridden = overriddenWords.has(`${item.sentenceIndex}-${item.wordIndex}`) || 
                                                    (item.override && item.override.isOverridden);
                            
                            return (
                              <TableRow 
                                key={index} 
                                hover
                                sx={{
                                  backgroundColor: isWordOverridden ? '#e8f5e8' : 'inherit',
                                  border: isWordOverridden ? '2px solid #4caf50' : 'none',
                                  '&:hover': {
                                    backgroundColor: isWordOverridden ? '#d4edda' : 'rgba(0, 0, 0, 0.04)'
                                  }
                                }}
                              >
                                <TableCell sx={{ fontWeight: 500, position: 'relative' }}>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    {item.targetWord}
                                    {isWordOverridden && (
                                      <Chip 
                                        label="Teacher Override" 
                                        size="small"
                                        sx={{ 
                                          backgroundColor: '#4caf50', 
                                          color: 'white',
                                          fontWeight: 600,
                                          fontSize: '0.7rem',
                                          height: 20
                                        }}
                                      />
                                    )}
                                  </Box>
                                </TableCell>
                              <TableCell>
                                <Box display="flex" alignItems="center">
                                  <Typography variant="body2" sx={{ 
                                    color: item.type === 'omitted' ? '#ff5722' : 'inherit',
                                    fontStyle: item.type === 'omitted' ? 'italic' : 'normal'
                                  }}>
                                    {item.miscue}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={`${item.accuracy}%`}
                                  size="small"
                                  sx={{ 
                                    backgroundColor: getScoreBackground(item.accuracy),
                                    color: getScoreColor(item.accuracy),
                                    fontWeight: 600
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Box display="flex" gap={1} alignItems="center">
                                  {item.type === 'omitted' ? (
                                    <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                      No audio (word was not spoken)
                                    </Typography>
                                  ) : item.timing ? (
                                    <IconButton
                                      size="small"
                                      onClick={() => handlePlayAudio(item.audioKey, item.sentenceIndex, item.wordIndex)}
                                      sx={{ 
                                        backgroundColor: playingAudio === item.audioKey ? '#e3f2fd' : 'transparent',
                                        '&:hover': { backgroundColor: '#e3f2fd' }
                                      }}
                                      title="Play word audio"
                                    >
                                      {playingAudio === item.audioKey ? <PauseIcon /> : <PlayArrowIcon />}
                                    </IconButton>
                                  ) : (
                                    <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                      No audio available
                                    </Typography>
                                  )}
                                  
                                  <Button
                                    size="small"
                                    startIcon={<EditIcon />}
                                    onClick={() => openOverrideDialog({
                                      sentenceIndex: item.sentenceIndex,
                                      wordIndex: item.wordIndex,
                                      targetWord: item.targetWord,
                                      miscue: item.miscue,
                                      accuracy: item.accuracy
                                    })}
                                    sx={{ minWidth: 'auto', px: 1, fontSize: '0.7rem' }}
                                  >
                                    Override
                                  </Button>
                                </Box>
                              </TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                );
              };

              return (
                <Box>
                  {/* Summary Cards */}
                  <Box display="flex" gap={2} mb={4}>
                    {[
                      { label: 'Miscues', count: errorAnalysis.miscues.length, color: '#ff9800', icon: '🔄' },
                      { label: 'Omitted', count: errorAnalysis.omittedWords.length, color: '#f44336', icon: '➖' },
                      { label: 'Self-Corrections', count: errorAnalysis.selfCorrections.length, color: '#2196f3', icon: '✓' },
                      { label: 'Other Issues', count: errorAnalysis.lowScoreWords.length, color: '#9c27b0', icon: '⚠️' }
                    ].map((stat) => (
                      <Paper key={stat.label} sx={{ flex: 1, p: 2, textAlign: 'center', border: `2px solid ${stat.color}20` }}>
                        <Typography sx={{ fontSize: '2rem', mb: 1 }}>{stat.icon}</Typography>
                        <Typography variant="h4" fontWeight="bold" color={stat.color}>
                          {stat.count}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {stat.label}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>

                  {/* Error Categories */}
                  <ErrorSection
                    title="Miscues"
                    items={errorAnalysis.miscues}
                    color="#ff9800"
                    icon="🔄"
                    description="Words that were read incorrectly or differently than intended"
                  />

                  <ErrorSection
                    title="Omitted Words"
                    items={errorAnalysis.omittedWords}
                    color="#f44336"
                    icon="➖"
                    description="Words that were skipped or not read aloud"
                  />

                  <ErrorSection
                    title="Self-Corrections"
                    items={errorAnalysis.selfCorrections}
                    color="#2196f3"
                    icon="✓"
                    description="Words that were initially read incorrectly but then corrected"
                  />

                  <ErrorSection
                    title="Other Issues"
                    items={errorAnalysis.lowScoreWords}
                    color="#9c27b0"
                    icon="⚠️"
                    description="Additional pronunciation or accuracy issues detected"
                  />

                  {/* Help Section */}
                  <Box mt={4} p={3} sx={{ backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                    <Typography variant="h6" mb={2} fontWeight={600}>Understanding the Analysis</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box mb={2}>
                          <Typography variant="body2" fontWeight={600} mb={0.5}>🔄 Miscues:</Typography>
                          <Typography variant="body2">Words pronounced differently than expected, often due to similar sounds or accent variations.</Typography>
                        </Box>
                        <Box mb={2}>
                          <Typography variant="body2" fontWeight={600} mb={0.5}>➖ Omitted Words:</Typography>
                          <Typography variant="body2">Words that were completely skipped during reading.</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box mb={2}>
                          <Typography variant="body2" fontWeight={600} mb={0.5}>✓ Self-Corrections:</Typography>
                          <Typography variant="body2">Shows reading awareness - student caught and fixed their own mistakes.</Typography>
                        </Box>
                        <Box mb={2}>
                          <Typography variant="body2" fontWeight={600} mb={0.5}>⚠️ Other Issues:</Typography>
                          <Typography variant="body2">Additional pronunciation challenges that need attention.</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              );
            })()}
          </Box>
        )}
      </Paper>
      
      {/* Regeneration Confirmation Dialog */}
      <Dialog open={regenerationDialog.open} onClose={closeRegenerationDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          textAlign: 'center'
        }}>
          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
            <RefreshIcon />
            <Typography variant="h6" fontWeight="bold">
              Regenerate {isStory ? 'Paragraph' : 'Sentence'} Analysis
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          {regenerationDialog.sentenceData && (
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={600} mb={1}>
                  🔄 This will regenerate the analysis for {isStory ? 'paragraph' : 'sentence'} {regenerationDialog.sentenceData.sentenceIndex + 1}
                </Typography>
                <Typography variant="body2">
                  • Audio will be re-transcribed with Whisper AI<br/>
                  • New analysis will be generated with OpenAI<br/>
                  • All current word overrides will be preserved<br/>
                  • You can view other parts of the report while this processes<br/>
                  • This process takes 30-60 seconds
                </Typography>
              </Alert>

              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} mb={1} color="primary">
                  Current {isStory ? 'Paragraph' : 'Sentence'} Text:
                </Typography>
                <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#555' }}>
                  "{regenerationDialog.sentenceData.sentenceText}"
                </Typography>
                
                {regenerationDialog.sentenceData.transcribedText && (
                  <>
                    <Typography variant="subtitle2" fontWeight={600} mt={2} mb={1} color="secondary">
                      Current Transcription:
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#9C27B0' }}>
                      "{regenerationDialog.sentenceData.transcribedText}"
                    </Typography>
                  </>
                )}
              </Box>

              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>⚠️ Warning:</strong> The new transcription and analysis may be different from the current results. 
                  Word overrides you've made will be preserved, but the underlying analysis will be refreshed.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={closeRegenerationDialog}
            variant="outlined"
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => {
              handleRegenerateSentence(regenerationDialog.sentenceData.sentenceIndex);
              closeRegenerationDialog();
            }}
            variant="contained"
            startIcon={<RefreshIcon />}
            sx={{ 
              minWidth: 140,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a67d8 0%, #6b63b5 100%)'
              }
            }}
          >
            Regenerate Now
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Override Dialog */}
      <Dialog open={overrideDialog.open} onClose={closeOverrideDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Override Miscue for "{overrideDialog.wordData?.targetWord}"
        </DialogTitle>
        <DialogContent>
          {overrideDialog.wordData && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Target Word:</strong> {overrideDialog.wordData.targetWord}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>System Detected:</strong> {overrideDialog.wordData.miscue}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                <strong>Current Accuracy:</strong> {overrideDialog.wordData.accuracy}%
              </Typography>
              
              <Typography variant="h6" sx={{ mb: 2 }}>Choose Override Type:</Typography>
              <RadioGroup value={overrideType} onChange={(e) => setOverrideType(e.target.value)}>
                <FormControlLabel 
                  value="correct" 
                  control={<Radio />} 
                  label="They read that correctly; the system misheard."
                />
                <FormControlLabel 
                  value="accent" 
                  control={<Radio />} 
                  label="They read it correctly using a language variant or an accent—this is not a mistake."
                />
                <FormControlLabel 
                  value="correction" 
                  control={<Radio />} 
                  label="They misread the word, but the miscue the system captured doesn't reflect what they actually said."
                />
              </RadioGroup>
              
              {overrideType === 'correction' && (
                <TextField
                  label="What did they actually say?"
                  value={correctedWord}
                  onChange={(e) => setCorrectedWord(e.target.value)}
                  fullWidth
                  sx={{ mt: 2 }}
                  required
                />
              )}
              
              <TextField
                label="Additional Notes (Optional)"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                fullWidth
                multiline
                rows={3}
                sx={{ mt: 2 }}
                placeholder="Any additional context or reasoning for this override..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeOverrideDialog}>Cancel</Button>
          <Button 
            onClick={handleOverrideSubmit} 
            variant="contained" 
            disabled={!overrideType || (overrideType === 'correction' && !correctedWord.trim())}
          >
            Apply Override
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
} 